'use client';
import React, { Fragment, useRef } from 'react';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components } from './PortableTextComponents';
import {
  FeatureProductCard,
  FeatureProductCardDetail,
} from '@/components/shared/cards';
import { FeatureCardType } from '@/components/shared/cards/FeatureProductCard';
import { m, useInView } from 'framer-motion';
import { fadeUp, moveUp } from '@/assets/framer/animations';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Scrollbar } from 'swiper/modules';
import { IKButton } from '@/components/shared/material';
import { ArrowRight } from '@phosphor-icons/react';
import { MenuItemLink } from '@/types/types';
import { AcceptedPaymentMethods } from '../shared/groupIcons';
import { testIds } from '@/constants/testIds';

export default function HeroBanner(props: {
  data?: {
    sectionId?: string;
    heading?: PortableTextBlock;
    type: 'fefature' | 'details';
    cards: FeatureCardType[];
    mainButton: {
      text: string;
      link: MenuItemLink;
    };
    displayOptions: {
      padding?: 'py' | 'pb' | 'pt';
      showPattern: boolean;
    };
  };
}) {
  const headingRef = useRef(null);
  const section = useRef(null);

  const isInView = useInView(headingRef, { amount: 0.4, once: true });
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const type = props.data?.type;
  const cards = props.data?.cards;
  const mainButton = props.data?.mainButton;
  const displayOptions = props.data?.displayOptions;

  // Swiper
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const slideClass = `${type == 'details' ? '!flex flex-col !h-full' : '!w-[unset]'} 2xl:max-w-[450px] lg:max-w-[420px] sm:max-w-[350px] max-w-[280px]`;
  const paginationButtonClass = `disabled:opacity-0 cursor-pointer [&_path]:fill-black fill-black h-12 w-12 shadow-lg absolute rounded-full bg-yellow hover:bg-yellow-500 flex items-center justify-center hover:scale-105 p-2.5`;

  const leftButton = `${paginationButtonClass} -left-2 swiper-button-outline-left`;
  const rightButton = `${paginationButtonClass} -right-2 swiper-button-outline-right`;

  return (
    <section
      id={sectionId}
      ref={section}
      /* Todo: add section padding configuration sectionPadding(displayOptions?.padding) */
      className={`bg-gradient-charcoal relative z-40 scroll-m-16 bg-gradient-to-b pt-5 sm:pt-0`}
    >
      {/* Top Divider wave */}
      <div className='absolute left-0 top-0 h-max w-full -translate-y-[calc(100%_-_1px)] transform overflow-hidden'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className='w-[200%] fill-charcoal-900 md:w-full'
          width='1464'
          height='34'
          viewBox='0 0 1464 34'
          fill='none'
          preserveAspectRatio='none'
        >
          <path
            className='fill-charcoal-900'
            d='M0 34H1464V13.9567C1462.14 14.0919 1459.76 14.2755 1456.86 14.4993L1456.79 14.5044C1400.39 18.8525 1148.67 38.2577 731.531 12.6924C315.789 -12.7874 65.0374 7.19974 7.26685 11.8046C4.35205 12.0369 1.92871 12.23 0 12.3763V34Z'
            fill='none'
          />
        </svg>
      </div>
      <div className='overflow-hidden'>
        <div className='container-padding container relative mx-auto'>
          <div className='flex flex-col items-stretch gap-x-7 md:flex-row md:justify-start'>
            <div
              className={`relative z-10 flex flex-grow flex-col justify-start md:w-1/2 md:pt-6 ${!(mainButton?.text && mainButton?.link?.url) && 'pb-9 md:pb-20'}`}
            >
              {displayOptions?.showPattern && (
                <>
                  {/* Pattern  */}
                  <m.svg
                    transition={{ delay: 0.1, type: 'tween' }}
                    initial='hidden'
                    animate={isInView ? 'visible' : 'hidden'}
                    variants={fadeUp}
                    xmlns='http://www.w3.org/2000/svg'
                    className='absolute -right-12 top-32 z-0 h-28 w-28 sm:h-40 sm:w-40'
                    width='170'
                    height='163'
                    viewBox='0 0 170 163'
                    fill='none'
                  >
                    <path
                      d='M92.2585 73.8117C92.7361 73.8117 93.1182 73.4292 93.1182 72.9511V60.788C93.1182 60.788 93.08 60.7498 93.08 60.7115H105.098V72.6834C105.098 73.1615 105.48 73.5631 105.977 73.5631H117.746V85.0568C117.746 85.5349 118.147 85.9365 118.625 85.9365H130.012V97.086H118.625C118.147 97.086 117.746 97.4685 117.746 97.9658V109.459H106.168C105.69 109.459 105.289 109.842 105.289 110.339V122.483C105.289 122.483 105.327 122.541 105.327 122.56H93.3666C93.4239 122.445 93.5004 122.349 93.5004 122.235V110.071C93.5004 109.593 93.1182 109.192 92.6406 109.192H81.0814V97.7745C81.0814 97.2964 80.6993 96.9139 80.2216 96.9139H68.4905V86.2617C68.4905 86.2617 68.4332 86.1469 68.414 86.0895H79.935C80.4127 86.0895 80.7948 85.707 80.7948 85.2289V73.8117H92.2776H92.2585ZM42.7546 72.1861C42.2769 72.1861 41.8948 72.5686 41.8948 73.0467V85.2098C41.8948 85.2098 41.9521 85.3246 41.9712 85.3819H29.9535C29.4758 85.3819 29.0746 85.7835 29.0746 86.2617V98.4056C29.0746 98.8837 29.4567 99.2853 29.9535 99.2853H42.2578V109.938C42.2578 110.416 42.6399 110.798 43.1176 110.798H54.4475V122.215C54.4475 122.694 54.8296 123.076 55.3073 123.076H67.325C67.2104 123.248 67.0766 123.439 67.0766 123.65V135.316C67.0766 135.928 67.5734 136.444 68.1848 136.444H79.5529C79.5529 136.444 79.5147 136.482 79.5147 136.52V148.684C79.5147 149.162 79.8968 149.563 80.3936 149.563H92.4304V162.071C92.4304 162.357 92.6597 162.587 92.9463 162.587H105.805C106.091 162.587 106.321 162.357 106.321 162.071V149.563H117.975C118.453 149.563 118.854 149.162 118.854 148.684V136.52C118.854 136.52 118.835 136.482 118.835 136.444H130.7C131.311 136.444 131.808 135.947 131.808 135.316V123.65C131.808 123.516 131.713 123.439 131.674 123.325H143.788C144.265 123.325 144.666 122.942 144.666 122.445V110.951H156.455C156.933 110.951 157.334 110.55 157.334 110.071V98.5969H168.492C168.969 98.5969 169.371 98.2144 169.371 97.7171V85.5541C169.371 85.076 168.969 84.6743 168.492 84.6743H156.971V72.8555C156.971 72.3774 156.589 71.9758 156.092 71.9758H144.303V60.4629C144.303 59.9848 143.921 59.6023 143.425 59.6023H132.133C132.133 59.6023 132.171 59.5449 132.171 59.5258V47.8791C132.171 47.2671 131.674 46.7507 131.044 46.7507H118.396C118.453 46.636 118.529 46.5595 118.529 46.4256V34.2625C118.529 33.7844 118.128 33.3828 117.65 33.3828H105.958V20.9329C105.958 20.646 105.728 20.4165 105.442 20.4165H92.5833C92.2967 20.4165 92.0674 20.646 92.0674 20.9329V33.4402H80.0306C79.5529 33.4402 79.1517 33.8227 79.1517 34.3199V46.483C79.1517 46.6169 79.2472 46.6934 79.2854 46.8081H67.688C67.0575 46.8081 66.5608 47.3053 66.5608 47.9365V59.5832C66.5608 59.7171 66.6563 59.7935 66.6945 59.9083H54.906C54.4284 59.9083 54.0272 60.2908 54.0272 60.7689V72.1861H42.6972H42.7546Z'
                      fill='#51504D'
                    />
                    <path
                      d='M26.6085 26.144H14.4379V14.7268H26.6085V26.144ZM0.910762 27.9035H13.559V39.6458C13.559 39.9327 13.7883 40.1622 14.0749 40.1622H27.0479C27.3345 40.1622 27.5638 39.9327 27.5638 39.6458V26.9664H40.0783C40.3649 26.9664 40.5941 26.7369 40.5941 26.45V13.4455C40.5941 13.1586 40.3649 12.9291 40.0783 12.9291H27.43V1.18677C27.43 0.899902 27.2008 0.67041 26.9142 0.67041H13.9411C13.6545 0.67041 13.4253 0.899902 13.4253 1.18677V13.8662H0.910762C0.624171 13.8662 0.394897 14.0957 0.394897 14.3826V27.368C0.394897 27.6549 0.624171 27.8844 0.910762 27.8844'
                      fill='#51504D'
                    />
                  </m.svg>
                </>
              )}

              <m.h2
                initial='hidden'
                animate={isInView ? 'visible' : 'hidden'}
                variants={fadeUp}
                ref={headingRef}
                className='heading-xl relative z-10 max-w-sm font-display font-medium tracking-tighter text-yellow-150 sm:max-w-4xl [&_strong]:text-yellow'
              >
                {heading && (
                  <PortableText
                    components={components}
                    value={heading}
                  ></PortableText>
                )}
              </m.h2>

              {cards && cards.length > 0 && (
                <>
                  {cards.length > 1 ? (
                    <m.div
                      className='relative z-10 mt-6 md:mt-16'
                      initial='outOfView'
                      animate={isInView ? 'visible' : 'outOfView'}
                      variants={moveUp}
                    >
                      <Swiper
                        className='!flex !h-full flex-col !overflow-visible [&>.swiper-wrapper]:my-10 [&>.swiper-wrapper]:space-x-5 [&>.swiper-wrapper]:overflow-visible lg:[&>.swiper-wrapper]:space-x-8 sm:[&_.swiper-pagination]:!bottom-8'
                        modules={[Navigation, Scrollbar, A11y]}
                        breakpoints={{
                          0: {
                            slidesPerView: 'auto',
                            spaceBetween: 0,
                          },
                          // when window width is >= 425px
                          425: {
                            slidesPerView: 'auto',
                            spaceBetween: 0,
                          },
                          // when window width is >= 765px
                          765: {
                            slidesPerView: 'auto',
                            spaceBetween: 0,
                          },
                          // when window width is >= 980px
                          980: {
                            slidesPerView: 'auto',
                            spaceBetween: 0,
                          },
                        }}
                        navigation={{
                          prevEl: prevRef.current,
                          nextEl: nextRef.current,
                          disabledClass: 'disabled',
                        }}
                      >
                        {cards.map((card, index) => (
                          <SwiperSlide
                            className={slideClass}
                            data-test-qa={`${testIds.purchaseJourneyCard}-${index}`}
                            key={`feature-product-card-${type}-${index}`}
                          >
                            <Fragment>
                              {type === 'details' ? (
                                <FeatureProductCardDetail
                                  index={index}
                                  card={card}
                                />
                              ) : (
                                <FeatureProductCard index={index} card={card} />
                              )}
                            </Fragment>
                          </SwiperSlide>
                        ))}
                        <div className='absolute left-0 top-[50%] z-40 block w-full -translate-y-8 transform'>
                          <button ref={prevRef} className={leftButton}>
                            <svg
                              className='h-5 w-5 rotate-180 transform'
                              xmlns='http://www.w3.org/2000/svg'
                              width='17'
                              height='16'
                              viewBox='0 0 17 16'
                              fill='none'
                            >
                              <path
                                d='M9.81223 15.4466L16.2143 9.0506C16.3186 8.95578 16.4019 8.84051 16.4589 8.7121C16.5159 8.58369 16.5453 8.44494 16.5453 8.30466C16.5453 8.16438 16.5159 8.02564 16.4589 7.89723C16.4019 7.76881 16.3186 7.65354 16.2143 7.55872L9.81223 1.16268C9.62179 0.977924 9.37156 0.865835 9.10577 0.84623C8.83997 0.826625 8.57574 0.900765 8.35979 1.05554C8.23002 1.14171 8.12192 1.25621 8.04381 1.39025C7.96569 1.52429 7.91964 1.67429 7.9092 1.82873C7.89876 1.98316 7.9242 2.13791 7.98357 2.28108C8.04294 2.42424 8.13466 2.55201 8.25167 2.65456L12.8785 7.23732L1.47524 7.23732C1.19718 7.248 0.934093 7.36495 0.741174 7.56365C0.548254 7.76235 0.440491 8.02735 0.440491 8.30306C0.440491 8.57878 0.548254 8.84378 0.741174 9.04248C0.934093 9.24118 1.19718 9.35813 1.47524 9.3688L12.8801 9.3688L8.25328 13.9532C8.13627 14.0557 8.04455 14.1835 7.98518 14.3266C7.92581 14.4698 7.90037 14.6246 7.91081 14.779C7.92126 14.9334 7.96731 15.0834 8.04542 15.2175C8.12354 15.3515 8.23164 15.466 8.36141 15.5522C8.57736 15.707 8.84159 15.7811 9.10738 15.7615C9.37317 15.7419 9.62341 15.6298 9.81385 15.445L9.81223 15.4466Z'
                                fill='none'
                              />
                            </svg>
                          </button>
                          <button ref={nextRef} className={rightButton}>
                            <svg
                              className='h-5 w-5'
                              xmlns='http://www.w3.org/2000/svg'
                              width='17'
                              height='16'
                              viewBox='0 0 17 16'
                              fill='none'
                            >
                              <path
                                d='M9.81223 15.4466L16.2143 9.0506C16.3186 8.95578 16.4019 8.84051 16.4589 8.7121C16.5159 8.58369 16.5453 8.44494 16.5453 8.30466C16.5453 8.16438 16.5159 8.02564 16.4589 7.89723C16.4019 7.76881 16.3186 7.65354 16.2143 7.55872L9.81223 1.16268C9.62179 0.977924 9.37156 0.865835 9.10577 0.84623C8.83997 0.826625 8.57574 0.900765 8.35979 1.05554C8.23002 1.14171 8.12192 1.25621 8.04381 1.39025C7.96569 1.52429 7.91964 1.67429 7.9092 1.82873C7.89876 1.98316 7.9242 2.13791 7.98357 2.28108C8.04294 2.42424 8.13466 2.55201 8.25167 2.65456L12.8785 7.23732L1.47524 7.23732C1.19718 7.248 0.934093 7.36495 0.741174 7.56365C0.548254 7.76235 0.440491 8.02735 0.440491 8.30306C0.440491 8.57878 0.548254 8.84378 0.741174 9.04248C0.934093 9.24118 1.19718 9.35813 1.47524 9.3688L12.8801 9.3688L8.25328 13.9532C8.13627 14.0557 8.04455 14.1835 7.98518 14.3266C7.92581 14.4698 7.90037 14.6246 7.91081 14.779C7.92126 14.9334 7.96731 15.0834 8.04542 15.2175C8.12354 15.3515 8.23164 15.466 8.36141 15.5522C8.57736 15.707 8.84159 15.7811 9.10738 15.7615C9.37317 15.7419 9.62341 15.6298 9.81385 15.445L9.81223 15.4466Z'
                                fill='none'
                              />
                            </svg>
                          </button>
                        </div>
                      </Swiper>
                    </m.div>
                  ) : (
                    <div className='mt-12 grid items-stretch gap-8 md:mt-20 md:grid-cols-2 md:gap-6 lg:gap-8 xl:flex xl:gap-10 2xl:gap-12'>
                      {cards &&
                        cards.length > 0 &&
                        cards.map((card, index) => (
                          <Fragment key={index}>
                            {type === 'details' ? (
                              <>
                                <FeatureProductCardDetail
                                  index={index}
                                  card={card}
                                />
                              </>
                            ) : (
                              <>
                                <FeatureProductCard index={index} card={card} />
                              </>
                            )}
                          </Fragment>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {mainButton?.text && mainButton?.link?.url && (
            <div
              className={`mt-5 flex w-full pb-9 md:mt-5 md:pb-20 [&>a]:!flex-grow`}
            >
              <IKButton
                link={mainButton?.link}
                className={`group flex w-full cursor-pointer items-center justify-center gap-3 border border-gray-600 !bg-[#282825] !text-[#EFE9DB] !shadow-none hover:!bg-charcoal-800 sm:w-auto`}
              >
                <span>{mainButton?.text}</span>
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full bg-yellow text-charcoal duration-150 ease-out group-hover:ml-2 [&_path]:!stroke-charcoal [&_path]:!stroke-[8px]`}
                >
                  <ArrowRight weight='bold' />
                </span>
              </IKButton>
            </div>
          )}
          <div className='px-4 pb-9 sm:hidden md:mt-5 md:pb-20'>
            <AcceptedPaymentMethods color='white' className='w-full' />
          </div>
        </div>
      </div>

      {/* Bottom Divider wave */}
      <div className='absolute bottom-0 left-0 h-max w-full translate-y-[calc(100%_-_1px)] transform overflow-hidden'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className='w-[200%] md:w-full [&_path]:fill-charcoal'
          width='1464'
          height='26'
          viewBox='0 0 1464 26'
          fill='none'
          preserveAspectRatio='none'
        >
          <path
            d='M0 0V13.4014C71.6641 8.27045 311.629 -4.51068 731.531 15.2506C1153.28 35.099 1393.51 22.1175 1464 17.0324V0H0Z'
            fill='none'
          />
        </svg>
      </div>
    </section>
  );
}
